import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import Axios from "@/lib/axios-instance";
import useOnboardingData from "@/store/onboarding-data";
import { useNavigate } from "react-router";
import { useUpdatePermission } from "../permissions/update-permission";

type APIPayload = {
    store_id: string;
    email: string;
    name: string;
    heard_from?: string;
    isPrevBot?: boolean;
    prev_bot?: string;
};

export const addUser = async (payload: APIPayload) => {
    try {
        const response = await Axios.post("/users/create-user", payload);
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(`${error.response.data?.meta?.message || "An error occurred"}`);
        }
        throw new Error(error.message || "Network Error");
    }
};

export const useAddUser = () => {
    const { mutate: addPermission } = useUpdatePermission();
    const { setIsStep2Complete, setStoreData, setUserData } = useOnboardingData();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    return useMutation({
        mutationFn: (payload: APIPayload) => addUser(payload),
        onSuccess: (data) => {
            queryClient.refetchQueries({ queryKey: ["check_user"] });
            setIsStep2Complete(true);
            setStoreData(data?.data?.store);
            setUserData(data?.data?.user);
            addPermission({
                data: [
                    {
                        module_id: "b9631bfd-a13f-40eb-b4b2-a77a13cd3a15",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "f1d86b8e-6b91-4985-b968-a09a1e8906eb",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "21b8924c-7846-41de-bfde-6cb4e20a13ed",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "71794f22-dd53-4d78-9e75-acbc54714e6c",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "4d200895-4571-4ee6-80d9-16367aceb3a4",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "de69ca63-ecae-4256-a7be-1ff17832cbce",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "d35da011-6589-4032-b14e-10f677b0013c",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "99e1ca6b-d8e2-427d-887a-eccca89d0939",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "79feddb9-6826-4ee7-8e09-b6500a4cd268",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "94a47ed9-15cd-457c-9ef4-a4c85856bfb0",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "f387835b-0570-4f77-8b39-a4f6aead0e8d",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "f2de7f69-7fcd-4ed8-a1b4-e5696327ad46",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                    {
                        module_id: "e9595fc9-067b-4d1e-a5b2-dcbb59409953",
                        user_id: data?.data?.id,
                        can_get_list: true,
                        can_get_one: true,
                        can_create: true,
                        can_update: true,
                        can_delete: true,
                    },
                ],
            });
            navigate("/onboarding/step-3", { replace: true });
        },
        onError: (error) => {
            toast.error("Unable to add user.", { description: error.message });
            console.log({ error });
        },
    });
};
